import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';
import { useAuth } from '../context/AuthContext';
import DatePicker, { getTodayISO } from './DatePicker';
import AssigneeMultiSelect from './AssigneeMultiSelect';
import MentionTextarea from './MentionTextarea';
import TeamSelect from './TeamSelect';
import CustomerHistoryPanel from './CustomerHistoryPanel';
import CustomerDevices from './CustomerDevices';
import { CASE_GLASSES_PAIRED_OPTIONS, PRODUCT_TYPE_OPTIONS, GLASSES_VERSION_OPTIONS, LENS_TYPE_OPTIONS, LABEL_OPTIONS } from '../constants/orderFieldOptions';

// Keep in sync with MAX_EMAIL_ATTACHMENTS_BYTES in tickets/sender_accounts.py.
const MAX_REPLY_ATTACHMENTS_BYTES = 20 * 1024 * 1024;
const formatSize = (bytes) => (bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`);

const TicketDetailModal = ({ ticketId, board, onClose, onChanged, onDeleted }) => {
  const { employee: currentEmployee } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(false);

  const activeTeam = teamId ? teams.find((t) => t.id === teamId) : null;
  // Team only narrows the *Assignees* picker (who's doing the work) --
  // Report To is deliberately left showing everyone, since who a ticket
  // escalates/reports to (e.g. a manager) is often outside the team doing
  // the work. Must never hide someone already assigned just because
  // they're outside the team, so the assignee picker adds back its own
  // current selections on top of the team-filtered list.
  const assigneePickerEmployees = (selectedIds) => {
    if (!activeTeam) return employees;
    const inTeam = employees.filter((e) => activeTeam.members.some((m) => m.mavis_user_id === e.mavis_user_id));
    const missingSelected = employees.filter((e) => selectedIds.includes(e.mavis_user_id) && !inTeam.includes(e));
    return [...inTeam, ...missingSelected];
  };

  const [commentTab, setCommentTab] = useState('internal'); // 'internal' | 'customer'
  const [commentBody, setCommentBody] = useState('');
  const [logAsCustomer, setLogAsCustomer] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [taggedIds, setTaggedIds] = useState([]);
  const [commentFiles, setCommentFiles] = useState([]);

  const [attachmentTab, setAttachmentTab] = useState('internal'); // 'internal' | 'customer'
  const [uploading, setUploading] = useState(false);

  const [showShipmentForm, setShowShipmentForm] = useState(false);
  const [shippingProfiles, setShippingProfiles] = useState([]);
  const [shipmentForm, setShipmentForm] = useState(null); // null until the form's first opened
  const [generatingShipment, setGeneratingShipment] = useState(false);
  // Real pickup dates from Parcel2Go (see api.getShipmentOptions) -- null
  // means "haven't looked yet" (falls back to a plain date input),
  // otherwise a list of {date, surcharge}. Reset to null whenever the
  // address/weight/profile changes so a stale list can't be booked
  // against a shipment it was never actually quoted for.
  const [shipmentDates, setShipmentDates] = useState(null);
  // Every matching Collection-type service Parcel2Go quoted (e.g. Royal
  // Mail 24hr Tracked AND 48hr Tracked show up as separate entries) --
  // backs the Service dropdown so staff can pick a speed/courier instead
  // of only ever getting the auto-picked default. Populated by the same
  // "Find real pickup dates" lookup as shipmentDates.
  const [shipmentServices, setShipmentServices] = useState([]);
  const [loadingShipmentDates, setLoadingShipmentDates] = useState(false);
  const [refreshingShipment, setRefreshingShipment] = useState(false);
  const [cancellingShipment, setCancellingShipment] = useState(false);

  const [showReturnForm, setShowReturnForm] = useState(false);
  const [returnForm, setReturnForm] = useState(null);
  const [generatingReturn, setGeneratingReturn] = useState(false);
  const [returnDates, setReturnDates] = useState(null);
  const [returnServices, setReturnServices] = useState([]);
  const [loadingReturnDates, setLoadingReturnDates] = useState(false);
  const [refreshingReturn, setRefreshingReturn] = useState(false);
  const [cancellingReturn, setCancellingReturn] = useState(false);

  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [descriptionTaggedIds, setDescriptionTaggedIds] = useState([]);
  // Kept in lockstep with descriptionDraft synchronously (see
  // handleDescriptionChange) so handleDescriptionTagsChange can read the
  // just-typed text immediately, without waiting on React's async state
  // batching -- selecting a mention inserts text AND tags in the same
  // synchronous call, and both need to land in the same save.
  const descriptionDraftRef = useRef('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getTicket(ticketId);
      setTicket(data);
      setDescriptionDraft(data.description || '');
      descriptionDraftRef.current = data.description || '';
      setDescriptionTaggedIds((data.description_tagged_employees || []).map((e) => e.mavis_user_id));
      // Opening the ticket clears the "you were tagged" marker server-side;
      // push the fresh (now-cleared) tagged_for_me back into the board's
      // own ticket list so the badge disappears from the card right away.
      // NOTE: onClose/onChanged are deliberately left out of this
      // useCallback's deps -- they're new inline functions on every
      // BoardPage render, and this callback is only meant to re-run when
      // ticketId itself changes (i.e. a genuinely different ticket is
      // opened), not on every parent re-render. Including them would
      // recompute `load`, re-trigger the mount effect below, and loop.
      onChanged(data);
    } catch (error) {
      toast.error('Could not load ticket.');
      onClose();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(() => {
    load();
    api.getEmployees().then(setEmployees).catch(() => {});
    api.getTeams().then(setTeams).catch(() => {});
  }, [load]);

  const patchField = async (fields) => {
    try {
      setSavingField(true);
      const updated = await api.updateTicket(ticketId, fields);
      setTicket(updated);
      onChanged(updated);
    } catch (error) {
      toast.error('Could not save change.');
    } finally {
      setSavingField(false);
    }
  };

  const handleAssign = async (newIds) => {
    const updated = await api.assignTicket(ticketId, newIds);
    setTicket(updated);
    onChanged(updated);
  };

  const handleReportTo = async (newIds) => {
    const updated = await api.setReportTo(ticketId, newIds);
    setTicket(updated);
    onChanged(updated);
  };

  const handleToggleWorking = async () => {
    try {
      const updated = await api.toggleTicketWorking(ticketId);
      setTicket(updated);
      onChanged(updated);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not update.');
    }
  };

  const handleColumnChange = async (e) => {
    const updated = await api.moveTicket(ticketId, e.target.value);
    setTicket(updated);
    onChanged(updated);
  };

  const handleDescriptionChange = (text) => {
    setDescriptionDraft(text);
    descriptionDraftRef.current = text;
  };

  const handleDescriptionBlur = () => {
    if (descriptionDraftRef.current !== ticket.description) {
      patchField({ description: descriptionDraftRef.current, description_tagged_employee_ids: descriptionTaggedIds });
    }
  };

  const handleDescriptionTagsChange = (newIds) => {
    setDescriptionTaggedIds(newIds);
    patchField({ description: descriptionDraftRef.current, description_tagged_employee_ids: newIds });
  };

  const hasCustomer = !!ticket?.customer;
  const effectiveCommentTab = hasCustomer ? commentTab : 'internal';
  const effectiveAttachmentTab = hasCustomer ? attachmentTab : 'internal';

  const commentAttachmentBytes = commentFiles.reduce((sum, f) => sum + f.size, 0);
  const commentAttachmentsTooLarge = commentAttachmentBytes > MAX_REPLY_ATTACHMENTS_BYTES;

  const handleCommentFilesChosen = (e) => {
    const chosen = Array.from(e.target.files || []);
    setCommentFiles((prev) => [...prev, ...chosen]);
    e.target.value = ''; // lets picking the exact same file again register as a change
  };

  const removeCommentFile = (index) => setCommentFiles((prev) => prev.filter((_, i) => i !== index));

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentBody.trim()) return;
    if (commentAttachmentsTooLarge) {
      toast.error(`Attachments are too large (${formatSize(commentAttachmentBytes)}) -- keep them under ${formatSize(MAX_REPLY_ATTACHMENTS_BYTES)} combined.`);
      return;
    }
    try {
      setPostingComment(true);
      await api.addComment(ticketId, {
        body: commentBody.trim(),
        visibility: effectiveCommentTab,
        is_customer_reply: effectiveCommentTab === 'customer' && logAsCustomer,
        tagged_employee_ids: taggedIds,
        files: commentFiles,
      });
      setCommentBody('');
      setLogAsCustomer(false);
      setTaggedIds([]);
      setCommentFiles([]);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not post comment.');
    } finally {
      setPostingComment(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      await api.uploadAttachment(ticketId, file, effectiveAttachmentTab);
      await load();
    } catch (error) {
      toast.error('Could not upload file.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    await api.deleteAttachment(ticketId, attachmentId);
    await load();
  };

  const openShipmentForm = () => {
    // Best-effort prefill -- city/postcode now come straight from the
    // customer's own record on file (mavis_backend's structured
    // address_line_1/city/postcode, synced onto customers.Customer), so
    // no more fragile comma-splitting of a single free-text blob. Still
    // blank (rather than guessed at) for older customers who only ever
    // had the old single address field filled in. All fields stay
    // editable either way -- ticket.customer_address (a per-ticket
    // override) still wins over the customer's own profile if set.
    setShipmentForm({
      carrier: 'parcel2go',
      shipping_profile_id: '',
      weight_grams: '',
      package_format: 'Parcel',
      height_mm: '', width_mm: '', depth_mm: '',
      value_gbp: 150,
      service_slug: '',
      collection_date: '',
      recipient_name: ticket.customer?.display_name || '',
      address_line_1: ticket.customer_address || ticket.customer?.address || '',
      address_line_2: '', city: ticket.customer?.city || '', county: '', postcode: ticket.customer?.postcode || '', country_code: 'GB',
      phone: ticket.customer_contact_number || ticket.customer?.contact_number || '',
      email: ticket.customer?.email || '',
    });
    setShipmentDates(null);
    setShipmentServices([]);
    setShowShipmentForm(true);
    if (shippingProfiles.length === 0) {
      api.getShippingProfiles().then(setShippingProfiles).catch(() => {});
    }
  };

  const handlePickProfile = (profileId) => {
    const profile = shippingProfiles.find((p) => String(p.id) === String(profileId));
    setShipmentForm((f) => ({
      ...f,
      shipping_profile_id: profileId,
      weight_grams: profile ? profile.weight_grams : f.weight_grams,
      package_format: profile ? profile.package_format : f.package_format,
      height_mm: profile?.height_mm || '',
      width_mm: profile?.width_mm || '',
      depth_mm: profile?.depth_mm || '',
      value_gbp: profile?.value_gbp || f.value_gbp,
    }));
    // A different weight/profile can change which service (and therefore
    // which pickup dates) would actually be quoted -- clear out any dates/
    // services already found so staff don't book a date/service found for
    // a different parcel.
    setShipmentDates(null);
    setShipmentServices([]);
  };

  // Shared by the "Find real pickup dates" button and the Service
  // dropdown's onChange -- serviceSlugOverride lets the dropdown ask for
  // a SPECIFIC service's own dates/price immediately, without waiting on
  // React to re-render shipmentForm.service_slug first.
  const fetchShipmentOptions = async (serviceSlugOverride) => {
    if (!shipmentForm.address_line_1.trim() || !shipmentForm.city.trim() || !shipmentForm.postcode.trim()) {
      toast.error('Fill in address line 1, city, and postcode first.');
      return;
    }
    if (!shipmentForm.weight_grams) {
      toast.error('Pick a shipping profile or enter a weight first.');
      return;
    }
    try {
      setLoadingShipmentDates(true);
      const result = await api.getShipmentOptions(ticketId, {
        ...shipmentForm,
        service_slug: serviceSlugOverride || shipmentForm.service_slug || undefined,
        shipping_profile_id: shipmentForm.shipping_profile_id || undefined,
        height_mm: shipmentForm.height_mm || undefined,
        width_mm: shipmentForm.width_mm || undefined,
        depth_mm: shipmentForm.depth_mm || undefined,
        value_gbp: shipmentForm.value_gbp || undefined,
      });
      setShipmentServices(result.services || []);
      setShipmentDates(result.available_dates || []);
      setShipmentForm((f) => ({
        ...f,
        service_slug: result.service_slug || f.service_slug,
        collection_date: f.collection_date || result.available_dates?.[0]?.date || '',
      }));
      if (!result.available_dates?.length) {
        toast.error('Parcel2Go had no pickup dates available for this service right now -- you can still type a date manually.');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not look up services/pickup dates.');
    } finally {
      setLoadingShipmentDates(false);
    }
  };

  const handleCheckShipmentDates = () => fetchShipmentOptions();

  const handleChangeShipmentService = (slug) => {
    setShipmentForm((f) => ({ ...f, service_slug: slug, collection_date: '' }));
    fetchShipmentOptions(slug);
  };

  const handleRefreshShipment = async () => {
    try {
      setRefreshingShipment(true);
      const result = await api.refreshShipment(ticketId);
      setTicket(result);
      onChanged(result);
      toast.success('Checked Parcel2Go for updates.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not refresh from Parcel2Go.');
    } finally {
      setRefreshingShipment(false);
    }
  };

  const handleCancelShipment = async () => {
    const isRoyalMailShipment = ticket.shipment_carrier === 'royal_mail';
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      isRoyalMailShipment
        ? 'This clears the Royal Mail label from this ticket so you can generate a new one -- an unposted label isn\'t charged, so there\'s nothing else to cancel. Continue?'
        : "This only clears the shipment from this ticket so you can generate a new one -- Parcel2Go has no cancel/refund API, "
          + 'so if you need to cancel the actual booking, do that on parcel2go.com yourself. An email reminder will be sent. Continue?',
    )) {
      return;
    }
    try {
      setCancellingShipment(true);
      const result = await api.cancelShipment(ticketId);
      setTicket(result);
      onChanged(result);
      toast.success(
        isRoyalMailShipment
          ? 'Royal Mail shipment label cleared from this ticket.'
          : result.cancel_notify_emailed
            ? 'Shipment cleared from this ticket. A reminder email was sent to cancel it on Parcel2Go.'
            : 'Shipment cleared from this ticket -- could not send the reminder email, so cancel it on Parcel2Go directly.',
      );
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not cancel the shipment.');
    } finally {
      setCancellingShipment(false);
    }
  };

  const handleGenerateShipment = async (e) => {
    e.preventDefault();
    const isRoyalMailShipment = shipmentForm.carrier === 'royal_mail';
    if (!shipmentForm.address_line_1.trim() || !shipmentForm.city.trim() || !shipmentForm.postcode.trim()) {
      toast.error('Address line 1, city, and postcode are all required.');
      return;
    }
    if (!shipmentForm.weight_grams) {
      toast.error('Pick a shipping profile or enter a weight.');
      return;
    }
    try {
      setGeneratingShipment(true);
      const result = await api.generateShipment(ticketId, {
        ...shipmentForm,
        shipping_profile_id: shipmentForm.shipping_profile_id || undefined,
        height_mm: shipmentForm.height_mm || undefined,
        width_mm: shipmentForm.width_mm || undefined,
        depth_mm: shipmentForm.depth_mm || undefined,
        collection_date: shipmentForm.collection_date || undefined,
      });
      setTicket(result);
      onChanged(result);
      setShowShipmentForm(false);
      if (isRoyalMailShipment && result.warning) {
        // Order was created on Royal Mail's account (and is now saved to
        // this ticket) but the label isn't back yet -- see
        // _generate_royal_mail_shipment's docstring. Surfaced as a warning
        // rather than the success toast so staff know to check back,
        // instead of assuming nothing happened at all.
        toast.error(result.warning, { duration: 8000 });
      } else {
        toast.success(
          isRoyalMailShipment
            ? `Royal Mail shipment label generated${result.tracking_number ? ` -- tracking ${result.tracking_number}` : ''}.`
            : `Shipment booked${result.shipment_service_name ? ` with ${result.shipment_service_name}` : ''}`
              + `${result.shipment_collection_date ? ` -- collection ${result.shipment_collection_date}` : ''}.`,
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not generate the shipment.');
    } finally {
      setGeneratingShipment(false);
    }
  };

  const openReturnForm = () => {
    setReturnForm({
      carrier: 'parcel2go',
      shipping_profile_id: '',
      weight_grams: '',
      package_format: 'Parcel',
      height_mm: '', width_mm: '', depth_mm: '',
      value_gbp: 150,
      service_slug: '',
      collection_date: '',
      recipient_name: ticket.customer?.display_name || '',
      address_line_1: ticket.customer_address || ticket.customer?.address || '',
      address_line_2: '', city: ticket.customer?.city || '', county: '', postcode: ticket.customer?.postcode || '', country_code: 'GB',
      phone: ticket.customer_contact_number || ticket.customer?.contact_number || '',
      email: ticket.customer?.email || '',
    });
    setReturnDates(null);
    setReturnServices([]);
    setShowReturnForm(true);
    if (shippingProfiles.length === 0) {
      api.getShippingProfiles().then(setShippingProfiles).catch(() => {});
    }
  };

  const handlePickReturnProfile = (profileId) => {
    const profile = shippingProfiles.find((p) => String(p.id) === String(profileId));
    setReturnForm((f) => ({
      ...f,
      shipping_profile_id: profileId,
      weight_grams: profile ? profile.weight_grams : f.weight_grams,
      package_format: profile ? profile.package_format : f.package_format,
      height_mm: profile?.height_mm || '',
      width_mm: profile?.width_mm || '',
      depth_mm: profile?.depth_mm || '',
      value_gbp: profile?.value_gbp || f.value_gbp,
    }));
    setReturnDates(null);
    setReturnServices([]);
  };

  const fetchReturnOptions = async (serviceSlugOverride) => {
    if (!returnForm.address_line_1.trim() || !returnForm.city.trim() || !returnForm.postcode.trim()) {
      toast.error('Fill in the customer\'s address line 1, city, and postcode first.');
      return;
    }
    if (!returnForm.weight_grams) {
      toast.error('Pick a shipping profile or enter a weight first.');
      return;
    }
    try {
      setLoadingReturnDates(true);
      const result = await api.getReturnOptions(ticketId, {
        ...returnForm,
        service_slug: serviceSlugOverride || returnForm.service_slug || undefined,
        shipping_profile_id: returnForm.shipping_profile_id || undefined,
        height_mm: returnForm.height_mm || undefined,
        width_mm: returnForm.width_mm || undefined,
        depth_mm: returnForm.depth_mm || undefined,
        value_gbp: returnForm.value_gbp || undefined,
      });
      setReturnServices(result.services || []);
      setReturnDates(result.available_dates || []);
      setReturnForm((f) => ({
        ...f,
        service_slug: result.service_slug || f.service_slug,
        collection_date: f.collection_date || result.available_dates?.[0]?.date || '',
      }));
      if (!result.available_dates?.length) {
        toast.error('Parcel2Go had no pickup dates available for this service right now -- you can still type a date manually.');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not look up services/pickup dates.');
    } finally {
      setLoadingReturnDates(false);
    }
  };

  const handleCheckReturnDates = () => fetchReturnOptions();

  const handleChangeReturnService = (slug) => {
    setReturnForm((f) => ({ ...f, service_slug: slug, collection_date: '' }));
    fetchReturnOptions(slug);
  };

  const handleRefreshReturn = async () => {
    try {
      setRefreshingReturn(true);
      const result = await api.refreshReturnLabel(ticketId);
      setTicket(result);
      onChanged(result);
      toast.success('Checked Parcel2Go for updates.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not refresh from Parcel2Go.');
    } finally {
      setRefreshingReturn(false);
    }
  };

  const handleCancelReturn = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      "This only clears the return collection from this ticket so you can book a new one -- Parcel2Go has no cancel/refund API, "
      + 'so if you need to cancel the actual booking, do that on parcel2go.com yourself. An email reminder will be sent. Continue?',
    )) {
      return;
    }
    try {
      setCancellingReturn(true);
      const result = await api.cancelReturnLabel(ticketId);
      setTicket(result);
      onChanged(result);
      toast.success(
        result.cancel_notify_emailed
          ? 'Return collection cleared from this ticket. A reminder email was sent to cancel it on Parcel2Go.'
          : 'Return collection cleared from this ticket -- could not send the reminder email, so cancel it on Parcel2Go directly.',
      );
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not cancel the return collection.');
    } finally {
      setCancellingReturn(false);
    }
  };

  const handleGenerateReturnLabel = async (e) => {
    e.preventDefault();
    const isRoyalMail = returnForm.carrier === 'royal_mail';
    if (!returnForm.address_line_1.trim() || !returnForm.city.trim() || !returnForm.postcode.trim()) {
      toast.error('Address line 1, city, and postcode are all required.');
      return;
    }
    if (!returnForm.email.trim()) {
      toast.error('A customer email address is required to send the return confirmation to.');
      return;
    }
    if (!isRoyalMail && !returnForm.weight_grams) {
      toast.error('Pick a shipping profile or enter a weight.');
      return;
    }
    if (!isRoyalMail && !returnForm.collection_date) {
      toast.error('Pick a collection date for the courier.');
      return;
    }
    try {
      setGeneratingReturn(true);
      const result = await api.generateReturnLabel(ticketId, {
        ...returnForm,
        shipping_profile_id: returnForm.shipping_profile_id || undefined,
        height_mm: returnForm.height_mm || undefined,
        width_mm: returnForm.width_mm || undefined,
        depth_mm: returnForm.depth_mm || undefined,
      });
      setTicket(result);
      onChanged(result);
      setShowReturnForm(false);
      toast.success(
        isRoyalMail
          ? `Royal Mail return label generated${result.return_service_name ? ` (${result.return_service_name})` : ''}.`
            + (result.emailed_to_customer ? ' Emailed to the customer.' : ' Could not email it -- check the logs.')
          : `Collection booked${result.return_service_name ? ` with ${result.return_service_name}` : ''}`
            + `${result.return_collection_date ? ` for ${result.return_collection_date}` : ''}.`
            + (result.emailed_to_customer ? ' Emailed to the customer.' : ' Could not email it -- check the logs.'),
      );
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not generate the return label.');
    } finally {
      setGeneratingReturn(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this ticket? This cannot be undone.')) return;
    await api.deleteTicket(ticketId);
    toast.success('Ticket deleted.');
    onDeleted(ticketId);
    onClose();
  };

  if (loading || !ticket) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const visibleComments = ticket.comments.filter((c) => c.visibility === effectiveCommentTab);
  const visibleAttachments = ticket.attachments.filter((a) => a.visibility === effectiveAttachmentTab);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 820 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="ticket-key" style={{ fontSize: '0.85rem' }}>{ticket.key}</span>
            {ticket.raised_by_customer && <span className="customer-raised-chip" style={{ marginLeft: '0.5rem' }}>RAISED BY CUSTOMER</span>}
            {ticket.last_reply_from === 'customer' && (
              <span className="label-chip" style={{ marginLeft: '0.5rem', background: 'rgba(255,176,32,0.2)', color: '#ffcf7a' }}>
                Customer replied
              </span>
            )}
            <h2 style={{ margin: '0.25rem 0 0' }}>{ticket.title}</h2>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="modal-grid">
          <div>
            <div className="form-group">
              <label className="form-label" htmlFor="td-title">Title</label>
              <input
                id="td-title"
                className="form-input"
                defaultValue={ticket.title}
                onBlur={(e) => e.target.value.trim() && e.target.value !== ticket.title && patchField({ title: e.target.value.trim() })}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="td-desc">Description</label>
              <MentionTextarea
                id="td-desc"
                minHeight={90}
                placeholder="Type @ to tag someone"
                value={descriptionDraft}
                onChange={handleDescriptionChange}
                employees={employees}
                taggedIds={descriptionTaggedIds}
                onTaggedIdsChange={handleDescriptionTagsChange}
                onBlur={handleDescriptionBlur}
              />
            </div>

            {hasCustomer && (
              <div className="form-group">
                <label className="form-label">Customer</label>
                <div className="form-input">
                  {ticket.customer.mavis_user_id ? (
                    <a href={`https://office.mavistech.uk/customer/${ticket.customer.mavis_user_id}`} target="_blank" rel="noopener noreferrer" title="Open this user in the office portal">
                      {ticket.customer.display_name} ↗
                    </a>
                  ) : ticket.customer.display_name}
                  {ticket.customer.email && <> &lt;{ticket.customer.email}&gt;</>}
                  {ticket.customer.is_adhoc && <span className="label-chip" style={{ marginLeft: '0.5rem' }}>One-off contact</span>}
                </div>
              </div>
            )}

            {hasCustomer && ticket.customer.id && (
              <CustomerDevices customerId={ticket.customer.id} mavisUserId={ticket.customer.mavis_user_id} />
            )}

            {hasCustomer && ticket.customer.id && (
              <CustomerHistoryPanel customerId={ticket.customer.id} currentTicketId={ticketId} />
            )}

            {hasCustomer && (
              <div className="checkbox-row">
                <input
                  type="checkbox"
                  id="td-notify-customer"
                  checked={!!ticket.notify_customer}
                  onChange={(e) => patchField({ notify_customer: e.target.checked })}
                />
                <label htmlFor="td-notify-customer">Notify customer by email about status changes and replies</label>
              </div>
            )}

            <div className="section-title">Comments</div>
            {hasCustomer && (
              <div className="segmented-control">
                <button type="button" className={commentTab === 'internal' ? 'active' : ''} onClick={() => setCommentTab('internal')}>
                  Internal notes
                </button>
                <button type="button" className={commentTab === 'customer' ? 'active' : ''} onClick={() => setCommentTab('customer')}>
                  Customer conversation
                </button>
              </div>
            )}

            {visibleComments.length === 0 && <p className="empty-state" style={{ padding: '1rem 0' }}>No {effectiveCommentTab === 'customer' ? 'customer conversation' : 'internal notes'} yet.</p>}
            {visibleComments.map((c) => (
              <div key={c.id} className="comment">
                <div className="comment-meta">
                  {c.is_customer_reply ? (ticket.customer?.display_name || 'Customer') : (c.author?.display_name || 'Unknown')}
                  {' '}&middot; {new Date(c.created_at).toLocaleString()}
                  {c.is_customer_reply && <span className="label-chip" style={{ marginLeft: '0.4rem' }}>Customer</span>}
                  {c.tagged_employees && c.tagged_employees.length > 0 && (
                    <span className="label-chip" style={{ marginLeft: '0.4rem' }}>
                      tagged {c.tagged_employees.map((e) => e.display_name || e.username).join(', ')}
                    </span>
                  )}
                </div>
                <div>{c.body}</div>
                {ticket.attachments.filter((a) => a.comment === c.id).map((a) => (
                  <a key={a.id} href={a.file} target="_blank" rel="noreferrer" style={{ fontSize: '0.78rem', display: 'block', marginTop: '0.2rem' }}>
                    📎 {a.original_filename}
                  </a>
                ))}
              </div>
            ))}

            <form onSubmit={handleAddComment} style={{ marginTop: '0.5rem' }}>
              {effectiveCommentTab === 'customer' && (
                <div className="checkbox-row">
                  <input
                    type="checkbox"
                    id="log-as-customer"
                    checked={logAsCustomer}
                    onChange={(e) => setLogAsCustomer(e.target.checked)}
                  />
                  <label htmlFor="log-as-customer">Log this as the customer's own reply (from a call or email)</label>
                </div>
              )}
              <MentionTextarea
                id="td-comment-body"
                minHeight={60}
                placeholder={effectiveCommentTab === 'customer' ? 'Reply to the customer, or log what they said... (type @ to tag someone)' : 'Add an internal note... (type @ to tag someone)'}
                value={commentBody}
                onChange={setCommentBody}
                employees={employees}
                taggedIds={taggedIds}
                onTaggedIdsChange={setTaggedIds}
                disabled={postingComment}
              />
              {effectiveCommentTab === 'customer' && !logAsCustomer && (
                <div style={{ marginTop: '0.4rem' }}>
                  <input type="file" multiple onChange={handleCommentFilesChosen} disabled={postingComment} style={{ fontSize: '0.85rem' }} />
                  {commentFiles.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', marginTop: '0.3rem' }}>
                      {commentFiles.map((file, i) => (
                        <div key={`${file.name}-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                          <span>{file.name} <span style={{ color: '#aab7c4' }}>({formatSize(file.size)})</span></span>
                          <button type="button" className="btn-secondary" style={{ padding: '0.1rem 0.5rem' }} onClick={() => removeCommentFile(i)}>Remove</button>
                        </div>
                      ))}
                      <p style={{ fontSize: '0.75rem', color: commentAttachmentsTooLarge ? '#e05555' : '#aab7c4', margin: 0 }}>
                        Total: {formatSize(commentAttachmentBytes)}{commentAttachmentsTooLarge ? ` -- over the ${formatSize(MAX_REPLY_ATTACHMENTS_BYTES)} limit, remove something` : ''} -- sent as a real email attachment
                      </p>
                    </div>
                  )}
                </div>
              )}
              <button type="submit" className="btn-secondary" style={{ marginTop: '0.5rem' }} disabled={postingComment || !commentBody.trim()}>
                {postingComment ? 'Posting...' : 'Comment'}
              </button>
            </form>

            <div className="section-title">Attachments</div>
            {hasCustomer && (
              <div className="segmented-control">
                <button type="button" className={attachmentTab === 'internal' ? 'active' : ''} onClick={() => setAttachmentTab('internal')}>
                  Internal only
                </button>
                <button type="button" className={attachmentTab === 'customer' ? 'active' : ''} onClick={() => setAttachmentTab('customer')}>
                  Customer-visible
                </button>
              </div>
            )}
            {visibleAttachments.length === 0 && <p className="empty-state" style={{ padding: '0.5rem 0' }}>No {effectiveAttachmentTab} attachments yet.</p>}
            {visibleAttachments.map((a) => (
              <div key={a.id} className="attachment-row">
                <a href={a.file} target="_blank" rel="noreferrer">{a.original_filename}</a>
                <button
                  type="button"
                  className="btn-danger"
                  style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
                  onClick={() => handleDeleteAttachment(a.id)}
                  aria-label={`Delete ${a.original_filename}`}
                >
                  Remove
                </button>
              </div>
            ))}
            <input type="file" onChange={handleUpload} disabled={uploading} style={{ marginTop: '0.5rem' }} />
          </div>

          <div>
            <div className="form-group">
              <label className="form-label" htmlFor="td-column">Status</label>
              <select id="td-column" className="form-select" value={ticket.column} onChange={handleColumnChange}>
                {board.columns.map((col) => (
                  <option key={col.id} value={col.id}>{col.name}</option>
                ))}
              </select>
              <div className="checkbox-row" style={{ marginTop: '0.4rem' }}>
                <input
                  type="checkbox"
                  id="td-completed"
                  checked={!!ticket.is_completed}
                  onChange={(e) => patchField({ is_completed: e.target.checked })}
                />
                <label htmlFor="td-completed">Completed (hides this ticket, regardless of status)</label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="td-team">Team</label>
              <TeamSelect id="td-team" teams={teams} teamId={teamId} onChange={setTeamId} />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="td-assignee">Assignees</label>
              <AssigneeMultiSelect
                id="td-assignee"
                employees={assigneePickerEmployees(ticket.assignees.map((a) => a.mavis_user_id))}
                selectedIds={ticket.assignees.map((a) => a.mavis_user_id)}
                onChange={handleAssign}
              />
              {(ticket.active_workers || []).length > 0 && (
                <p style={{ fontSize: '0.75rem', color: '#aab7c4', margin: '0.3rem 0 0' }}>
                  Working on it now: {ticket.active_workers.map((e) => e.display_name).join(', ')}
                </p>
              )}
              {currentEmployee && ticket.assignees.some((a) => a.mavis_user_id === currentEmployee.mavis_user_id) && (
                <button
                  type="button"
                  className={ticket.active_workers?.some((e) => e.mavis_user_id === currentEmployee.mavis_user_id) ? 'btn-primary' : 'btn-secondary'}
                  style={{ marginTop: '0.4rem', fontSize: '0.8rem' }}
                  onClick={handleToggleWorking}
                >
                  {ticket.active_workers?.some((e) => e.mavis_user_id === currentEmployee.mavis_user_id) ? "✓ I'm on it -- tap to step off" : "I'm on it"}
                </button>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="td-report-to">Report to</label>
              <AssigneeMultiSelect
                id="td-report-to"
                employees={employees}
                selectedIds={(ticket.report_to || []).map((e) => e.mavis_user_id)}
                onChange={handleReportTo}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="td-priority">Priority</label>
              <select
                id="td-priority"
                className="form-select"
                defaultValue={ticket.priority}
                onChange={(e) => patchField({ priority: e.target.value })}
              >
                <option value="highest">Highest</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
                <option value="lowest">Lowest</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="td-due">Due date</label>
              <DatePicker
                id="td-due"
                value={ticket.due_date || getTodayISO()}
                onChange={(v) => patchField({ due_date: v })}
                disabled={savingField || !currentEmployee?.can_edit_due_date}
              />
              {!currentEmployee?.can_edit_due_date && (
                <div style={{ fontSize: '0.72rem', color: '#aab7c4', marginTop: 4 }}>Locked: only David can change the due date.</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="td-labels">Label</label>
              <select
                id="td-labels"
                className="form-select"
                value={ticket.labels || ''}
                onChange={(e) => patchField({ labels: e.target.value })}
              >
                <option value="">—</option>
                {LABEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Reporter</label>
              <div className="form-input">{ticket.reporter?.display_name || '—'}</div>
            </div>

            <div className="section-title">Tracking</div>
            <div className="form-group">
              <label className="form-label" htmlFor="td-tracking_number">Tracking number</label>
              <input
                id="td-tracking_number"
                // Keyed on the current value so this uncontrolled input
                // actually remounts (picking up the new defaultValue)
                // whenever tracking_number changes from somewhere OTHER
                // than typing in this box -- e.g. generate_shipment fetching
                // it after payment, or the Refresh/Cancel buttons. Without
                // this key, React only applies defaultValue on first mount,
                // so the field kept showing stale/blank text even after the
                // ticket's real tracking number had been saved.
                key={ticket.tracking_number}
                className="form-input"
                defaultValue={ticket.tracking_number}
                onBlur={(e) => e.target.value !== ticket.tracking_number && patchField({ tracking_number: e.target.value })}
              />
            </div>

            <div className="section-title">Shipping</div>
            {ticket.shipped_at ? (
              <div className="form-group">
                <p style={{ fontSize: '0.85rem', margin: '0 0 0.3rem' }}>
                  {ticket.shipment_carrier === 'royal_mail' ? 'Royal Mail label generated' : 'Shipped'}{' '}
                  {new Date(ticket.shipped_at).toLocaleString()}
                  {ticket.shipping_profile_name ? ` -- ${ticket.shipping_profile_name}` : ''}
                  {ticket.shipment_service_name ? ` -- ${ticket.shipment_service_name}` : ''}
                  {ticket.shipment_collection_date ? ` -- collection ${ticket.shipment_collection_date}` : ''}
                  {ticket.shipment_carrier === 'royal_mail' && ticket.tracking_number ? ` -- tracking ${ticket.tracking_number}` : ''}
                </p>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {ticket.shipment_label && (
                    <a href={ticket.shipment_label} target="_blank" rel="noreferrer" className="btn-secondary" style={{ display: 'inline-block', textDecoration: 'none' }}>
                      Download label{ticket.shipment_carrier === 'royal_mail' ? '' : ' (A4)'}
                    </a>
                  )}
                  {ticket.shipment_label_4x6 && (
                    <a href={ticket.shipment_label_4x6} target="_blank" rel="noreferrer" className="btn-secondary" style={{ display: 'inline-block', textDecoration: 'none' }}>
                      Download label (4x6)
                    </a>
                  )}
                  {ticket.shipment_carrier !== 'royal_mail' && (!ticket.tracking_number || !ticket.shipment_label || !ticket.shipment_label_4x6) && (
                    <button type="button" className="btn-secondary" onClick={handleRefreshShipment} disabled={refreshingShipment}>
                      {refreshingShipment ? 'Checking...' : 'Refresh tracking/label'}
                    </button>
                  )}
                  <button type="button" className="btn-secondary" onClick={handleCancelShipment} disabled={cancellingShipment}>
                    {cancellingShipment ? 'Cancelling...' : ticket.shipment_carrier === 'royal_mail' ? 'Clear shipment label' : 'Cancel shipment'}
                  </button>
                </div>
                <p style={{ fontSize: '0.72rem', color: '#aab7c4', margin: '0.3rem 0 0' }}>
                  {ticket.shipment_carrier === 'royal_mail'
                    ? "This just clears the record here so you can generate a new one -- an unposted Royal Mail label isn't charged, so there's nothing else to cancel."
                    : "Cancel only clears this ticket's record so you can generate a new shipment -- it does not cancel "
                      + 'anything with Parcel2Go itself (their API has no cancel/refund option), so also cancel it on '
                      + 'parcel2go.com. An email reminder is sent automatically.'}
                </p>
              </div>
            ) : showShipmentForm ? (
              <form onSubmit={handleGenerateShipment} className="form-group">
                <label className="form-label">Carrier</label>
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.6rem' }}>
                  <button
                    type="button"
                    className={shipmentForm.carrier === 'parcel2go' ? 'btn-primary' : 'btn-secondary'}
                    style={{ flex: 1 }} disabled={generatingShipment}
                    onClick={() => setShipmentForm((f) => ({ ...f, carrier: 'parcel2go' }))}
                  >
                    Parcel2Go (courier collection)
                  </button>
                  <button
                    type="button"
                    className={shipmentForm.carrier === 'royal_mail' ? 'btn-primary' : 'btn-secondary'}
                    style={{ flex: 1 }} disabled={generatingShipment}
                    onClick={() => setShipmentForm((f) => ({ ...f, carrier: 'royal_mail' }))}
                  >
                    Royal Mail (self-service label)
                  </button>
                </div>
                {shipmentForm.carrier === 'royal_mail' && (
                  <p style={{ fontSize: '0.72rem', color: '#aab7c4', margin: '-0.3rem 0 0.5rem' }}>
                    Generates an instant Royal Mail label (the account's own default service) + tracking number --
                    no courier is booked; Mavis staff print it and post or drop the parcel off themselves.
                  </p>
                )}

                <label className="form-label" htmlFor="td-ship-profile">Shipping profile</label>
                <select
                  id="td-ship-profile" className="form-select" style={{ marginBottom: '0.4rem' }}
                  value={shipmentForm.shipping_profile_id} disabled={generatingShipment}
                  onChange={(e) => handlePickProfile(e.target.value)}
                >
                  <option value="">Custom (enter weight below)</option>
                  {shippingProfiles.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.weight_grams}g)</option>
                  ))}
                </select>

                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                  <input
                    className="form-input" type="number" style={{ flex: 1 }} placeholder="Weight (g)"
                    value={shipmentForm.weight_grams} disabled={generatingShipment}
                    onChange={(e) => setShipmentForm((f) => ({ ...f, weight_grams: e.target.value }))}
                  />
                  <input
                    className="form-input" type="number" style={{ flex: 1 }} placeholder="Height (mm)"
                    value={shipmentForm.height_mm} disabled={generatingShipment}
                    onChange={(e) => setShipmentForm((f) => ({ ...f, height_mm: e.target.value }))}
                  />
                  <input
                    className="form-input" type="number" style={{ flex: 1 }} placeholder="Width (mm)"
                    value={shipmentForm.width_mm} disabled={generatingShipment}
                    onChange={(e) => setShipmentForm((f) => ({ ...f, width_mm: e.target.value }))}
                  />
                  <input
                    className="form-input" type="number" style={{ flex: 1 }} placeholder="Depth (mm)"
                    value={shipmentForm.depth_mm} disabled={generatingShipment}
                    onChange={(e) => setShipmentForm((f) => ({ ...f, depth_mm: e.target.value }))}
                  />
                  {shipmentForm.carrier === 'parcel2go' && (
                    <input
                      className="form-input" type="number" style={{ flex: 1 }} placeholder="Value (£)"
                      title="Declared/insured value sent to Parcel2Go -- defaults to £150"
                      value={shipmentForm.value_gbp} disabled={generatingShipment}
                      onChange={(e) => setShipmentForm((f) => ({ ...f, value_gbp: e.target.value }))}
                    />
                  )}
                </div>

                {shipmentForm.carrier === 'parcel2go' && (
                  <>
                    <label className="form-label" htmlFor="td-ship-service">Service</label>
                    <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                      <select
                        id="td-ship-service" className="form-select" style={{ flex: 1 }}
                        value={shipmentForm.service_slug || ''} disabled={generatingShipment || loadingShipmentDates || shipmentServices.length === 0}
                        onChange={(e) => handleChangeShipmentService(e.target.value)}
                      >
                        {shipmentServices.length === 0 ? (
                          <option value="">Click "Find services & dates" below</option>
                        ) : (
                          shipmentServices.map((s) => (
                            <option key={s.service_slug} value={s.service_slug}>
                              {s.service_name}{s.price != null ? ` -- £${Number(s.price).toFixed(2)}` : ''}
                            </option>
                          ))
                        )}
                      </select>
                      <button
                        type="button" className="btn-secondary" disabled={generatingShipment || loadingShipmentDates}
                        onClick={handleCheckShipmentDates}
                      >
                        {loadingShipmentDates ? 'Checking...' : shipmentServices.length > 0 ? 'Re-check' : 'Find services & dates'}
                      </button>
                    </div>
                    <p style={{ fontSize: '0.72rem', color: '#aab7c4', margin: '-0.2rem 0 0.4rem' }}>
                      Lets you pick between e.g. Royal Mail 24hr Tracked and 48hr Tracked (or another courier) -- pick a
                      service, then the pickup dates below refresh for that specific service.
                    </p>

                    <label className="form-label" htmlFor="td-ship-collection-date">Collection date (optional -- defaults to next working day)</label>
                    {shipmentDates && shipmentDates.length > 0 ? (
                      <select
                        id="td-ship-collection-date" className="form-select" style={{ marginBottom: '0.4rem' }}
                        value={shipmentForm.collection_date} disabled={generatingShipment}
                        onChange={(e) => setShipmentForm((f) => ({ ...f, collection_date: e.target.value }))}
                      >
                        {shipmentDates.map((d) => (
                          <option key={d.date} value={d.date}>
                            {d.date}{d.surcharge ? ` (+£${d.surcharge.toFixed(2)} surcharge)` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id="td-ship-collection-date"
                        className="form-input" type="date" style={{ marginBottom: '0.4rem' }}
                        value={shipmentForm.collection_date} disabled={generatingShipment}
                        onChange={(e) => setShipmentForm((f) => ({ ...f, collection_date: e.target.value }))}
                      />
                    )}
                  </>
                )}

                <input
                  className="form-input" style={{ marginBottom: '0.4rem' }} placeholder="Recipient name"
                  value={shipmentForm.recipient_name} disabled={generatingShipment}
                  onChange={(e) => setShipmentForm((f) => ({ ...f, recipient_name: e.target.value }))}
                />
                <input
                  className="form-input" style={{ marginBottom: '0.4rem' }} placeholder="Address"
                  value={shipmentForm.address_line_1} disabled={generatingShipment}
                  onChange={(e) => setShipmentForm((f) => ({ ...f, address_line_1: e.target.value }))}
                />
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                  <input
                    className="form-input" style={{ flex: 1 }} placeholder="City"
                    value={shipmentForm.city} disabled={generatingShipment}
                    onChange={(e) => setShipmentForm((f) => ({ ...f, city: e.target.value }))}
                  />
                  <input
                    className="form-input" style={{ flex: 1 }} placeholder="Postcode"
                    value={shipmentForm.postcode} disabled={generatingShipment}
                    onChange={(e) => setShipmentForm((f) => ({ ...f, postcode: e.target.value }))}
                  />
                </div>
                <input
                  className="form-input" style={{ margin: '0.4rem 0' }} placeholder="Address line 2 (optional)"
                  value={shipmentForm.address_line_2} disabled={generatingShipment}
                  onChange={(e) => setShipmentForm((f) => ({ ...f, address_line_2: e.target.value }))}
                />
                <input
                  className="form-input" style={{ marginBottom: '0.4rem' }} placeholder="County (optional)"
                  value={shipmentForm.county} disabled={generatingShipment}
                  onChange={(e) => setShipmentForm((f) => ({ ...f, county: e.target.value }))}
                />
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                  <input
                    className="form-input" style={{ flex: 1 }} placeholder="Phone (optional)"
                    value={shipmentForm.phone} disabled={generatingShipment}
                    onChange={(e) => setShipmentForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                  <input
                    className="form-input" style={{ flex: 1 }} placeholder="Email (optional)"
                    value={shipmentForm.email} disabled={generatingShipment}
                    onChange={(e) => setShipmentForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>

                <p style={{ fontSize: '0.75rem', color: '#f0a500', margin: '0 0 0.5rem' }}>
                  {shipmentForm.carrier === 'royal_mail'
                    ? 'Generates a real Royal Mail label instantly (weight is required; dimensions are optional) using '
                      + "the account's own default service -- no courier is booked, and an unposted label is not charged."
                    : 'This creates a real, billed Parcel2Go shipment (weight and dimensions are both required for a quote), '
                      + 'collected from Mavis\'s own address and picking the best matching service automatically. '
                      + 'Parcel2Go has no cancel/refund option -- if you need to cancel after booking, do it directly on '
                      + 'parcel2go.com or via Parcel2Go support.'}
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="submit" className="btn-primary" disabled={generatingShipment}>
                    {generatingShipment
                      ? (shipmentForm.carrier === 'royal_mail' ? 'Generating...' : 'Booking...')
                      : (shipmentForm.carrier === 'royal_mail' ? 'Generate shipment label' : 'Generate shipment')}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setShowShipmentForm(false)} disabled={generatingShipment}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="form-group">
                <button type="button" className="btn-secondary" onClick={openShipmentForm}>Generate shipment</button>
              </div>
            )}

            <div className="section-title">Return collection</div>
            <p style={{ fontSize: '0.75rem', color: '#aab7c4', margin: '-0.3rem 0 0.5rem' }}>
              For getting an item back from the customer (e.g. a repair). Choose Parcel2Go for a real
              courier collection from the customer's address (driver brings the label), or Royal Mail
              for a self-service prepaid label the customer posts back themselves.
            </p>
            {ticket.return_label_generated_at ? (
              <div className="form-group">
                <p style={{ fontSize: '0.85rem', margin: '0 0 0.3rem' }}>
                  {ticket.return_carrier === 'royal_mail' ? 'Royal Mail return label generated' : 'Collection booked'}{' '}
                  {new Date(ticket.return_label_generated_at).toLocaleString()}
                  {ticket.return_service_name ? ` -- ${ticket.return_service_name}` : ''}
                  {ticket.return_collection_date ? ` -- collection ${ticket.return_collection_date}` : ''}
                  {ticket.return_tracking_number ? ` -- tracking ${ticket.return_tracking_number}` : ''}
                </p>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {ticket.return_label && (
                    <a href={ticket.return_label} target="_blank" rel="noreferrer" className="btn-secondary" style={{ display: 'inline-block', textDecoration: 'none' }}>
                      Download return label{ticket.return_carrier === 'royal_mail' ? '' : ' (A4)'}
                    </a>
                  )}
                  {ticket.return_label_4x6 && (
                    <a href={ticket.return_label_4x6} target="_blank" rel="noreferrer" className="btn-secondary" style={{ display: 'inline-block', textDecoration: 'none' }}>
                      Download return label (4x6)
                    </a>
                  )}
                  {ticket.return_carrier !== 'royal_mail' && (!ticket.return_tracking_number || !ticket.return_label || !ticket.return_label_4x6) && (
                    <button type="button" className="btn-secondary" onClick={handleRefreshReturn} disabled={refreshingReturn}>
                      {refreshingReturn ? 'Checking...' : 'Refresh tracking/label'}
                    </button>
                  )}
                  <button type="button" className="btn-secondary" onClick={handleCancelReturn} disabled={cancellingReturn}>
                    {cancellingReturn ? 'Cancelling...' : ticket.return_carrier === 'royal_mail' ? 'Clear return label' : 'Cancel return collection'}
                  </button>
                </div>
                <p style={{ fontSize: '0.72rem', color: '#aab7c4', margin: '0.3rem 0 0' }}>
                  {ticket.return_carrier === 'royal_mail'
                    ? "This just clears the record here so you can generate a new one -- an unposted Royal Mail return label isn't charged, so there's nothing else to cancel."
                    : 'Cancel only clears this ticket\'s record so you can book a new collection -- it does not cancel '
                      + 'anything with Parcel2Go itself (their API has no cancel/refund option), so also cancel it on '
                      + 'parcel2go.com. An email reminder is sent automatically.'}
                </p>
              </div>
            ) : showReturnForm ? (
              <form onSubmit={handleGenerateReturnLabel} className="form-group">
                <label className="form-label">Carrier</label>
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.6rem' }}>
                  <button
                    type="button"
                    className={returnForm.carrier === 'parcel2go' ? 'btn-primary' : 'btn-secondary'}
                    style={{ flex: 1 }} disabled={generatingReturn}
                    onClick={() => setReturnForm((f) => ({ ...f, carrier: 'parcel2go' }))}
                  >
                    Parcel2Go (courier collection)
                  </button>
                  <button
                    type="button"
                    className={returnForm.carrier === 'royal_mail' ? 'btn-primary' : 'btn-secondary'}
                    style={{ flex: 1 }} disabled={generatingReturn}
                    onClick={() => setReturnForm((f) => ({ ...f, carrier: 'royal_mail' }))}
                  >
                    Royal Mail (self-service label)
                  </button>
                </div>
                {returnForm.carrier === 'royal_mail' && (
                  <p style={{ fontSize: '0.72rem', color: '#aab7c4', margin: '-0.3rem 0 0.5rem' }}>
                    Generates a prepaid Tracked Returns 48 label + tracking number instantly -- no courier is booked;
                    the customer prints it and drops the parcel at a Post Office, priority postbox, or parcel locker.
                  </p>
                )}

                {returnForm.carrier === 'parcel2go' && (
                  <>
                    <label className="form-label" htmlFor="td-return-profile">Shipping profile</label>
                    <select
                      id="td-return-profile" className="form-select" style={{ marginBottom: '0.4rem' }}
                      value={returnForm.shipping_profile_id} disabled={generatingReturn}
                      onChange={(e) => handlePickReturnProfile(e.target.value)}
                    >
                      <option value="">Custom (enter weight below)</option>
                      {shippingProfiles.map((p) => (
                        <option key={p.id} value={p.id}>{p.name} ({p.weight_grams}g)</option>
                      ))}
                    </select>

                    <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                      <input
                        className="form-input" type="number" style={{ flex: 1 }} placeholder="Weight (g)"
                        value={returnForm.weight_grams} disabled={generatingReturn}
                        onChange={(e) => setReturnForm((f) => ({ ...f, weight_grams: e.target.value }))}
                      />
                      <input
                        className="form-input" type="number" style={{ flex: 1 }} placeholder="Height (mm)"
                        value={returnForm.height_mm} disabled={generatingReturn}
                        onChange={(e) => setReturnForm((f) => ({ ...f, height_mm: e.target.value }))}
                      />
                  <input
                    className="form-input" type="number" style={{ flex: 1 }} placeholder="Width (mm)"
                    value={returnForm.width_mm} disabled={generatingReturn}
                    onChange={(e) => setReturnForm((f) => ({ ...f, width_mm: e.target.value }))}
                  />
                  <input
                    className="form-input" type="number" style={{ flex: 1 }} placeholder="Depth (mm)"
                    value={returnForm.depth_mm} disabled={generatingReturn}
                    onChange={(e) => setReturnForm((f) => ({ ...f, depth_mm: e.target.value }))}
                  />
                  <input
                    className="form-input" type="number" style={{ flex: 1 }} placeholder="Value (£)"
                    title="Declared/insured value sent to Parcel2Go -- defaults to £150"
                    value={returnForm.value_gbp} disabled={generatingReturn}
                    onChange={(e) => setReturnForm((f) => ({ ...f, value_gbp: e.target.value }))}
                  />
                </div>

                <label className="form-label" htmlFor="td-return-service">Service</label>
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                  <select
                    id="td-return-service" className="form-select" style={{ flex: 1 }}
                    value={returnForm.service_slug || ''} disabled={generatingReturn || loadingReturnDates || returnServices.length === 0}
                    onChange={(e) => handleChangeReturnService(e.target.value)}
                  >
                    {returnServices.length === 0 ? (
                      <option value="">Click "Find services & dates" below</option>
                    ) : (
                      returnServices.map((s) => (
                        <option key={s.service_slug} value={s.service_slug}>
                          {s.service_name}{s.price != null ? ` -- £${Number(s.price).toFixed(2)}` : ''}
                        </option>
                      ))
                    )}
                  </select>
                  <button
                    type="button" className="btn-secondary" disabled={generatingReturn || loadingReturnDates}
                    onClick={handleCheckReturnDates}
                  >
                    {loadingReturnDates ? 'Checking...' : returnServices.length > 0 ? 'Re-check' : 'Find services & dates'}
                  </button>
                </div>

                <label className="form-label" htmlFor="td-return-collection-date">Collection date</label>
                {returnDates && returnDates.length > 0 ? (
                  <select
                    id="td-return-collection-date" className="form-select" style={{ marginBottom: '0.4rem' }}
                    value={returnForm.collection_date} disabled={generatingReturn}
                    onChange={(e) => setReturnForm((f) => ({ ...f, collection_date: e.target.value }))}
                  >
                    {returnDates.map((d) => (
                      <option key={d.date} value={d.date}>
                        {d.date}{d.surcharge ? ` (+£${d.surcharge.toFixed(2)} surcharge)` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="td-return-collection-date"
                    className="form-input" type="date" style={{ marginBottom: '0.4rem' }}
                    value={returnForm.collection_date} disabled={generatingReturn}
                    onChange={(e) => setReturnForm((f) => ({ ...f, collection_date: e.target.value }))}
                  />
                )}
                  </>
                )}

                <input
                  className="form-input" style={{ marginBottom: '0.4rem' }} placeholder="Customer name"
                  value={returnForm.recipient_name} disabled={generatingReturn}
                  onChange={(e) => setReturnForm((f) => ({ ...f, recipient_name: e.target.value }))}
                />
                <p style={{ fontSize: '0.72rem', color: '#aab7c4', margin: '-0.2rem 0 0.4rem' }}>
                  {returnForm.carrier === 'royal_mail'
                    ? "This is the customer's own address -- printed on the label as who's sending the parcel back."
                    : "This is the customer's own address -- where the courier will collect the parcel from."}
                </p>
                <input
                  className="form-input" style={{ marginBottom: '0.4rem' }} placeholder="Address"
                  value={returnForm.address_line_1} disabled={generatingReturn}
                  onChange={(e) => setReturnForm((f) => ({ ...f, address_line_1: e.target.value }))}
                />
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                  <input
                    className="form-input" style={{ flex: 1 }} placeholder="City"
                    value={returnForm.city} disabled={generatingReturn}
                    onChange={(e) => setReturnForm((f) => ({ ...f, city: e.target.value }))}
                  />
                  <input
                    className="form-input" style={{ flex: 1 }} placeholder="Postcode"
                    value={returnForm.postcode} disabled={generatingReturn}
                    onChange={(e) => setReturnForm((f) => ({ ...f, postcode: e.target.value }))}
                  />
                </div>
                <input
                  className="form-input" style={{ margin: '0.4rem 0' }} placeholder="Address line 2 (optional)"
                  value={returnForm.address_line_2} disabled={generatingReturn}
                  onChange={(e) => setReturnForm((f) => ({ ...f, address_line_2: e.target.value }))}
                />
                <input
                  className="form-input" style={{ marginBottom: '0.4rem' }} placeholder="County (optional)"
                  value={returnForm.county} disabled={generatingReturn}
                  onChange={(e) => setReturnForm((f) => ({ ...f, county: e.target.value }))}
                />
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                  <input
                    className="form-input" style={{ flex: 1 }} placeholder="Phone (optional)"
                    value={returnForm.phone} disabled={generatingReturn}
                    onChange={(e) => setReturnForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                  <input
                    className="form-input" style={{ flex: 1 }} placeholder="Customer email"
                    value={returnForm.email} disabled={generatingReturn}
                    onChange={(e) => setReturnForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>

                <p style={{ fontSize: '0.75rem', color: '#f0a500', margin: '0 0 0.5rem' }}>
                  {returnForm.carrier === 'royal_mail'
                    ? 'Generates a real Royal Mail return label and emails it to the customer to print themselves -- '
                      + 'no courier is booked. An unposted label is not charged.'
                    : 'This books a real, billed courier collection -- and emails the customer a confirmation '
                      + '(no label to print; the driver brings it). Parcel2Go has no cancel/refund option -- if you need '
                      + 'to cancel after booking, do it directly on parcel2go.com or via Parcel2Go support.'}
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="submit" className="btn-primary" disabled={generatingReturn}>
                    {generatingReturn
                      ? (returnForm.carrier === 'royal_mail' ? 'Generating...' : 'Booking...')
                      : (returnForm.carrier === 'royal_mail' ? 'Generate return label' : 'Book return collection')}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setShowReturnForm(false)} disabled={generatingReturn}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="form-group">
                <button type="button" className="btn-secondary" onClick={openReturnForm}>Book return collection</button>
              </div>
            )}

            {board.key === 'OF' && (
              <>
                <div className="section-title">Order details</div>

                <div className="form-group">
                  <label className="form-label" htmlFor="td-order_number">Order number</label>
                  <input
                    id="td-order_number"
                    className="form-input"
                    defaultValue={ticket.order_number}
                    onBlur={(e) => e.target.value !== ticket.order_number && patchField({ order_number: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="td-case_and_glasses_paired">Case and Glasses Paired</label>
                  <select
                    id="td-case_and_glasses_paired"
                    className="form-select"
                    value={ticket.case_and_glasses_paired || ''}
                    onChange={(e) => patchField({ case_and_glasses_paired: e.target.value })}
                  >
                    <option value="">—</option>
                    {CASE_GLASSES_PAIRED_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="td-product_type">Product type</label>
                  <select
                    id="td-product_type"
                    className="form-select"
                    value={ticket.product_type || ''}
                    onChange={(e) => patchField({ product_type: e.target.value })}
                  >
                    <option value="">—</option>
                    {PRODUCT_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="td-glasses_version">Glasses version</label>
                  <select
                    id="td-glasses_version"
                    className="form-select"
                    value={ticket.glasses_version || ''}
                    onChange={(e) => patchField({ glasses_version: e.target.value })}
                  >
                    <option value="">—</option>
                    {GLASSES_VERSION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="td-lens_type">Lens type</label>
                  <select
                    id="td-lens_type"
                    className="form-select"
                    value={ticket.lens_type || ''}
                    onChange={(e) => patchField({ lens_type: e.target.value })}
                  >
                    <option value="">—</option>
                    {LENS_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>

                {[
                  ['glasses_sn', 'Glasses SN'],
                  ['case_sn', 'Case SN'],
                ].map(([field, label]) => (
                  <div className="form-group" key={field}>
                    <label className="form-label" htmlFor={`td-${field}`}>{label}</label>
                    <input
                      id={`td-${field}`}
                      className="form-input"
                      defaultValue={ticket[field]}
                      onBlur={(e) => e.target.value !== ticket[field] && patchField({ [field]: e.target.value })}
                    />
                  </div>
                ))}
              </>
            )}

            <button type="button" className="btn-danger" style={{ width: '100%', marginTop: '1rem' }} onClick={handleDelete}>
              Delete ticket
            </button>
            {savingField && <p style={{ fontSize: '0.75rem', color: '#aab7c4' }}>Saving...</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailModal;
